import { getCommonParams, buildContentId } from './gaParams';

import { analyticsConsent } from './analyticsConsent';

const gtag = (_command, event, params) => analyticsConsent.track(event, params);

// ── page_view ──────────────────────────────────────────
export const trackPageView = (
  {
    pageType,
    contentType,
    contentId,
    contentIdPrefix,
    contentName,
    referrerType,
    referrerSection,
    artistSection,
    reviewsCategory,
    reviewsSection,
  },
  user,
) => {
  gtag('event', 'page_view', {
    ...getCommonParams(user),
    page_type: pageType,
    referrer_type: referrerType ?? 'direct',
    ...(user?.id && { login_user_id: user.id }),
    ...(contentType && { content_type: contentType }),
    ...(contentType &&
      contentId && { content_id: buildContentId(contentIdPrefix ?? contentType, contentId) }),
    ...(contentName && { content_name: contentName }),
    ...(referrerSection && { referrer_section: referrerSection }),
    ...(artistSection && { artist_section: artistSection }),
    ...(reviewsCategory && { reviews_category: reviewsCategory }),
    ...(reviewsSection && { reviews_section: reviewsSection }),
  });
};

// ── content_like  (로그인 유저만) ──────────────────────
export const trackContentLike = ({ contentType, contentId, pageType }, user) => {
  if (!user) return;
  gtag('event', 'content_like', {
    ...getCommonParams(user),
    content_id: buildContentId(contentType, contentId),
    ...(pageType && { page_type: pageType }),
  });
};

// ── content_save  (로그인 유저만) ──────────────────────
export const trackContentSave = ({ contentType, contentId, contentName, pageType }, user) => {
  if (!user) return;
  gtag('event', 'content_save', {
    ...getCommonParams(user),
    content_type: contentType,
    content_id: buildContentId(contentType, contentId),
    save_count: 1,
    ...(contentName && { content_name: contentName }),
    ...(pageType && { page_type: pageType }),
  });
};

// ── comment_create  (로그인 유저만) ───────────────────
export const trackCommentCreate = ({ contentType, contentId, contentName, pageType }, user) => {
  if (!user) return;
  gtag('event', 'comment_create', {
    ...getCommonParams(user),
    content_type: contentType,
    content_id: buildContentId(contentType, contentId),
    comment_count: 1,
    ...(contentName && { content_name: contentName }),
    ...(pageType && { page_type: pageType }),
  });
};

// ── review_create  (로그인 유저만) ────────────────────
// contentId: Apple Music sourceId (page_view의 content_id와 통일)
export const trackReviewCreate = ({ contentId, contentName, pageType }, user) => {
  if (!user) return;
  gtag('event', 'review_create', {
    ...getCommonParams(user),
    content_type: 'album',
    content_id: buildContentId('album', contentId),
    review_count: 1,
    ...(contentName && { content_name: contentName }),
    ...(pageType && { page_type: pageType }),
  });
};

// ── share_click ───────────────────────────────────────
export const trackShareClick = ({ contentType, contentId, contentName, pageType }, user) => {
  gtag('event', 'share_click', {
    ...getCommonParams(user),
    content_id: buildContentId(contentType, contentId),
    share_count: 1,
    ...(contentName && { content_name: contentName }),
    ...(pageType && { page_type: pageType }),
  });
};

// ── search ────────────────────────────────────────────
export const trackSearch = ({ searchTerm, pageType }, user) => {
  gtag('event', 'search', {
    ...getCommonParams(user),
    search_term: searchTerm,
    ...(pageType && { page_type: pageType }),
  });
};

// ── menu_click ────────────────────────────────────────
// pageType: 클릭 시 현재 페이지, targetPageType: 이동할 메뉴
export const trackMenuClick = ({ targetPageType, pageType }, user) => {
  gtag('event', 'menu_click', {
    ...getCommonParams(user),
    target_page_type: targetPageType,
    ...(pageType && { page_type: pageType }),
  });
};

// ── external_link_click ───────────────────────────────
export const trackExternalLinkClick = ({ linkType, contentType, contentId, pageType }, user) => {
  gtag('event', 'external_link_click', {
    ...getCommonParams(user),
    link_type: linkType,
    ...(contentType && contentId && { content_id: buildContentId(contentType, contentId) }),
    ...(pageType && { page_type: pageType }),
  });
};

// ── home_content_click ────────────────────────────────
// section: HOME_SECTION 값 (featured / recommended / news / lists)
export const trackHomeContentClick = ({ section, contentType, contentId, contentName }, user) => {
  gtag('event', 'home_content_click', {
    ...getCommonParams(user),
    page_type: 'home',
    section,
    ...(contentType && contentId && { content_id: buildContentId(contentType, contentId) }),
    ...(contentName && { content_name: contentName }),
  });
};

// ── list_content_click ────────────────────────────────
// contentType: CONTENT_TYPE.LIST_ALBUM | CONTENT_TYPE.LIST_SONG (어떤 리스트인지)
// actionType: LIST_ACTION_TYPE 값 (album_click / artist_click)
export const trackListContentClick = (
  { contentType, actionType, contentId, contentName },
  user,
) => {
  gtag('event', 'list_content_click', {
    ...getCommonParams(user),
    page_type: 'list_post',
    content_type: contentType,
    action_type: actionType,
    ...(contentId && { content_id: contentId }),
    ...(contentName && { content_name: contentName }),
  });
};

// ── review_content_click ──────────────────────────────
// section: REVIEW_SECTION 값 (issues / recommended_album / user_reviews)
export const trackReviewContentClick = ({ section, contentType, contentId, contentName }, user) => {
  gtag('event', 'review_content_click', {
    ...getCommonParams(user),
    page_type: 'reviews_post',
    section,
    ...(contentType && contentId && { content_id: buildContentId(contentType, contentId) }),
    ...(contentName && { content_name: contentName }),
  });
};

// ── artist_content_click ──────────────────────────────
// artistSection: ARTIST_SECTION 값 (full_albums / singles / ... / similar_artists)
// actionType: ARTIST_ACTION_TYPE 값 (album_click / view_all / artist_click)
// pageType: PAGE_TYPE.ARTIST | PAGE_TYPE.ARTIST_VIEW_ALL
export const trackArtistContentClick = (
  { artistSection, actionType, contentType, contentId, contentName, pageType },
  user,
) => {
  gtag('event', 'artist_content_click', {
    ...getCommonParams(user),
    page_type: pageType ?? 'artist',
    artist_section: artistSection,
    action_type: actionType,
    ...(contentType && contentId && { content_id: buildContentId(contentType, contentId) }),
    ...(contentName && { content_name: contentName }),
  });
};

// ── banner_click ──────────────────────────────────────
export const trackBannerClick = ({ bannerId, bannerTitle, bannerLocation, pageType }, user) => {
  gtag('event', 'banner_click', {
    ...getCommonParams(user),
    banner_id: bannerId,
    ...(bannerTitle && { banner_title: bannerTitle }),
    ...(bannerLocation && { banner_location: bannerLocation }),
    ...(pageType && { page_type: pageType }),
  });
};

// ── banner_impression ─────────────────────────────────
export const trackBannerImpression = ({ bannerId, bannerTitle, bannerLocation }, user) => {
  gtag('event', 'banner_impression', {
    ...getCommonParams(user),
    banner_id: bannerId,
    ...(bannerTitle && { banner_title: bannerTitle }),
    ...(bannerLocation && { banner_location: bannerLocation }),
  });
};

// ── artist_name_click ──────────────────────────────────
// 어떤 페이지에서든 아티스트 이름 텍스트 링크를 클릭할 때 발생
export const trackArtistNameClick = ({ contentId, contentName, pageType }, user) => {
  gtag('event', 'artist_name_click', {
    ...getCommonParams(user),
    content_type: 'artist',
    ...(contentId && { content_id: buildContentId('artist', contentId) }),
    ...(contentName && { content_name: contentName }),
    ...(pageType && { page_type: pageType }),
  });
};

// ── user_profile_click ────────────────────────────────
// 리뷰/댓글 작성자 닉네임 클릭 시 유저 프로필로 이동할 때 발생
export const trackUserProfileClick = ({ contentName, pageType }, user) => {
  gtag('event', 'user_profile_click', {
    ...getCommonParams(user),
    content_type: 'user',
    ...(contentName && { content_name: contentName }),
    ...(pageType && { page_type: pageType }),
  });
};

// ── adsense_click ─────────────────────────────────────
export const trackAdsenseClick = ({ adType, pageType }, user) => {
  gtag('event', 'adsense_click', {
    ...getCommonParams(user),
    ad_type: adType,
    ...(pageType && { page_type: pageType }),
  });
};
